/**
 * EnergyHistoryPanel — ENERGY-HISTORY-24H.
 *
 * Painel "Energia 24h" exibido na linha expandida da página Dispositivos, só
 * para devices com INA226. Lazy-load: busca o histórico ao montar (= ao expandir
 * a linha). Gráfico de potência (W) SVG hand-rolled (sem lib), tooltip com
 * V/A/W/amostras, cards de resumo, estado de histórico parcial / vazio / erro.
 */
import { useEffect, useMemo, useRef, useState } from "react";

import { getEnergyHistory } from "../lib/api";

const W = 640;
const H = 150;
const PAD_L = 38;
const PAD_T = 12;
const PAD_B = 22;
const LINE = "#2BD9F0"; // noc.accent (ciano) — linha de CORRENTE (A)
const V_LINE = "#35E8A4"; // noc.ok (verde) — linha de TENSÃO (V)

function fmtHm(ts) {
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}
const f1 = (n) => (n == null ? "—" : Number(n).toFixed(1));
const f2 = (n) => (n == null ? "—" : Number(n).toFixed(2));
const f3 = (n) => (n == null ? "—" : Number(n).toFixed(3));

function StatCard({ label, value, unit, accent }) {
  return (
    <div className="rounded-lg border border-noc-border/60 bg-noc-bg/50 px-3 py-2">
      <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-noc-faint">
        {label}
      </div>
      <div
        className={`mt-0.5 font-mono text-sm font-semibold ${
          accent ? "text-noc-accent" : "text-noc-text"
        }`}
      >
        {value}
        {unit && <span className="ml-0.5 text-[10px] text-noc-muted">{unit}</span>}
      </div>
    </div>
  );
}

// Chip compacto (resumo na lista) — leve p/ caber em mobile.
function StatChip({ label, value, unit, accent }) {
  return (
    <span className="inline-flex items-baseline gap-1 rounded border border-noc-border/60 bg-noc-bg/50 px-1.5 py-0.5">
      <span className="font-mono text-[8px] uppercase tracking-[0.1em] text-noc-faint">
        {label}
      </span>
      <span
        className={`font-mono text-[11px] font-semibold ${
          accent ? "text-noc-accent" : "text-noc-text"
        }`}
      >
        {value}
        {unit && <span className="ml-0.5 text-[9px] text-noc-muted">{unit}</span>}
      </span>
    </span>
  );
}

// Sparkline minimalista (sem eixos/tooltip) p/ o resumo compacto da lista.
function MiniSparkline({ points }) {
  const d = useMemo(() => {
    if (!points || points.length < 2) return null;
    const w = 240;
    const h = 36;
    const pad = 2;
    const ts = points.map((p) => new Date(p.ts).getTime());
    const tMin = ts[0];
    const tSpan = Math.max(1, ts[ts.length - 1] - tMin);
    const ps = points.map((p) => p.power_w);
    let mx = Math.max(...ps);
    let mn = Math.min(...ps);
    if (mx === mn) {
      mx += 0.5;
      mn = Math.max(0, mn - 0.5);
    }
    const x = (t) => pad + ((t - tMin) / tSpan) * (w - 2 * pad);
    const y = (v) => pad + (1 - (v - mn) / (mx - mn)) * (h - 2 * pad);
    return points.map((p, i) => `${x(ts[i])},${y(p.power_w)}`).join(" ");
  }, [points]);
  if (!d) return null;
  return (
    <svg
      viewBox="0 0 240 36"
      className="w-full"
      style={{ height: 36 }}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <polyline
        fill="none"
        stroke="#2BD9F0"
        strokeWidth="1.5"
        strokeLinejoin="round"
        points={d}
      />
    </svg>
  );
}

// ENERGY-DEVICE-CHART-1 — gráfico de DUAS linhas: Tensão (V, eixo esq.) e
// Corrente (A, eixo dir.), escalas independentes (V~12 e A~0.3 têm magnitudes
// diferentes). Tooltip mostra V, A e W do ponto. Quebra a linha em gaps.
function VACurrentChart({ points, bucketMinutes }) {
  const [hover, setHover] = useState(null);
  const svgRef = useRef(null);
  const PAD_RA = 34; // pad direito p/ os rótulos do eixo de Corrente (A)

  const geom = useMemo(() => {
    if (!points.length) return null;
    const ts = points.map((p) => new Date(p.ts).getTime());
    const tMin = ts[0];
    const tMax = ts[ts.length - 1];
    const tSpan = Math.max(1, tMax - tMin);
    const range = (arr) => {
      let mx = Math.max(...arr);
      let mn = Math.min(...arr);
      if (mx === mn) {
        const d = Math.abs(mx) * 0.05 || 0.5;
        mx += d;
        mn -= d;
      }
      const pad = (mx - mn) * 0.12 || 0.05;
      return { mn: mn - pad, mx: mx + pad };
    };
    const vR = range(points.map((p) => p.voltage_v));
    const aR = range(points.map((p) => p.current_a));
    const xOf = (t) => PAD_L + ((t - tMin) / tSpan) * (W - PAD_L - PAD_RA);
    const yOf = (val, r) =>
      PAD_T + (1 - (val - r.mn) / (r.mx - r.mn)) * (H - PAD_T - PAD_B);
    const xy = points.map((p, i) => ({
      p,
      x: xOf(ts[i]),
      yV: yOf(p.voltage_v, vR),
      yA: yOf(p.current_a, aR),
      t: ts[i],
    }));
    // Preserva gaps: quebra as linhas quando o intervalo > 3× bucket.
    const gapMs = bucketMinutes * 60 * 1000 * 3;
    const segments = [];
    let cur = [xy[0]];
    for (let i = 1; i < xy.length; i++) {
      if (ts[i] - ts[i - 1] > gapMs) {
        segments.push(cur);
        cur = [];
      }
      cur.push(xy[i]);
    }
    segments.push(cur);
    return { xy, segments, vR, aR, tMin, tMax };
  }, [points, bucketMinutes]);

  if (!geom) return null;
  const { xy, segments, vR, aR, tMin, tMax } = geom;

  function onMove(e) {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const svgX = ((e.clientX - rect.left) / rect.width) * W;
    let best = xy[0];
    for (const pt of xy) {
      if (Math.abs(pt.x - svgX) < Math.abs(best.x - svgX)) best = pt;
    }
    setHover({
      p: best.p,
      vx: best.x,
      vyV: best.yV,
      vyA: best.yA,
      cx: (best.x / W) * rect.width,
      cyTop: (Math.min(best.yV, best.yA) / H) * rect.height,
    });
  }

  const vTicks = [vR.mx, (vR.mx + vR.mn) / 2, vR.mn];
  const aTicks = [aR.mx, (aR.mx + aR.mn) / 2, aR.mn];

  return (
    <div className="relative mt-2">
      <div className="mb-1 flex gap-4 font-mono text-[10px]">
        <span style={{ color: V_LINE }}>● Tensão (V)</span>
        <span style={{ color: LINE }}>● Corrente (A)</span>
      </div>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="w-full"
        style={{ height: 160 }}
        preserveAspectRatio="none"
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
        role="img"
        aria-label="Gráfico de tensão e corrente das últimas 24h"
      >
        {/* grid + eixo Y esquerdo (V, verde) e direito (A, ciano) */}
        {[0, 1, 2].map((i) => {
          const yy = PAD_T + (i / 2) * (H - PAD_T - PAD_B);
          return (
            <g key={i}>
              <line x1={PAD_L} x2={W - PAD_RA} y1={yy} y2={yy} stroke="#1C2E49" strokeWidth="1" />
              <text x={2} y={yy + 3} fontSize="9" fill={V_LINE} fontFamily="monospace">
                {f1(vTicks[i])}
              </text>
              <text x={W - PAD_RA + 3} y={yy + 3} fontSize="9" fill={LINE} fontFamily="monospace">
                {f2(aTicks[i])}
              </text>
            </g>
          );
        })}
        {/* eixo X (tempo) */}
        <text x={PAD_L} y={H - 6} fontSize="9" fill="#5B7088" fontFamily="monospace">
          {fmtHm(tMin)}
        </text>
        <text x={W - PAD_RA} y={H - 6} fontSize="9" fill="#5B7088" fontFamily="monospace" textAnchor="end">
          {fmtHm(tMax)}
        </text>
        {/* duas linhas (segmentadas por gaps): tensão + corrente */}
        {segments.map((seg, si) => (
          <g key={si}>
            <polyline
              fill="none"
              stroke={V_LINE}
              strokeWidth="1.5"
              strokeLinejoin="round"
              points={seg.map((pt) => `${pt.x},${pt.yV}`).join(" ")}
            />
            <polyline
              fill="none"
              stroke={LINE}
              strokeWidth="1.5"
              strokeLinejoin="round"
              points={seg.map((pt) => `${pt.x},${pt.yA}`).join(" ")}
            />
          </g>
        ))}
        {/* marcadores do ponto sob o cursor (nas duas linhas) */}
        {hover && (
          <g>
            <line x1={hover.vx} x2={hover.vx} y1={PAD_T} y2={H - PAD_B} stroke="#1C2E49" strokeWidth="1" />
            <circle cx={hover.vx} cy={hover.vyV} r="3" fill={V_LINE} />
            <circle cx={hover.vx} cy={hover.vyA} r="3" fill={LINE} />
          </g>
        )}
      </svg>
      {hover && (
        <div
          className="pointer-events-none absolute z-10 rounded-md border border-noc-border bg-noc-bg/95 px-2 py-1 font-mono text-[10px] text-noc-text shadow-lg"
          style={{
            left: Math.min(Math.max(hover.cx - 50, 0), 540),
            top: Math.max(hover.cyTop - 60, 0),
          }}
        >
          <div className="text-noc-faint">{fmtHm(hover.p.ts)}</div>
          <div>
            <span style={{ color: V_LINE }}>{f2(hover.p.voltage_v)} V</span>
            {" · "}
            <span style={{ color: LINE }}>{f3(hover.p.current_a)} A</span>
          </div>
          <div className="text-noc-muted">
            {f2(hover.p.power_w)} W · {hover.p.samples} amostra(s)
          </div>
        </div>
      )}
    </div>
  );
}

export default function EnergyHistoryPanel({
  deviceId,
  variant = "full",
  onOpenDetail,
}) {
  const compact = variant === "compact";
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const sectionRef = useRef(null);

  // ENERGY-HISTORY-VISIBILITY — no resumo in-list (compact), traz o painel
  // para a viewport ao expandir. No DeviceDetail (full) já está na página.
  useEffect(() => {
    if (!compact) return undefined;
    const el = sectionRef.current;
    if (!el) return undefined;
    const id = setTimeout(
      () => el.scrollIntoView({ block: "nearest", behavior: "smooth" }),
      60,
    );
    return () => clearTimeout(id);
  }, [compact]);

  useEffect(() => {
    const ctrl = new AbortController();
    setState({ loading: true, error: null, data: null });
    getEnergyHistory(deviceId, { hours: 24, bucketMinutes: 5, signal: ctrl.signal })
      .then((data) => setState({ loading: false, error: null, data }))
      .catch((err) => {
        if (err?.name === "AbortError" || ctrl.signal.aborted) return;
        setState({ loading: false, error: err?.message || "Falha ao carregar", data: null });
      });
    return () => ctrl.abort();
  }, [deviceId]);

  const { loading, error, data } = state;

  // Device sem INA226: não renderiza painel (segurança extra — o pai já gata).
  if (data && data.supported === false) return null;

  const summary = data?.summary || null;
  const hasPoints = (data?.points?.length || 0) > 0;

  return (
    <section
      ref={sectionRef}
      className={`mt-3 w-full rounded-xl border border-noc-accent/30 bg-noc-bg/30 ${
        compact ? "p-2.5" : "p-3"
      }`}
    >
      <header className="mb-2 flex items-center justify-between gap-2">
        <h3 className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-noc-muted">
          ⚡ Energia 24h
        </h3>
        {data?.partial && hasPoints && (
          <span
            className="rounded border border-noc-warn/30 bg-noc-warn/10 px-1.5 py-0.5 text-[9px] font-semibold text-noc-warn"
            title="Ainda não há 24h completas de dados (sensor recém-ativado)."
          >
            histórico parcial — {f1(data.coverage_hours)} h de {data.hours} h
          </span>
        )}
      </header>

      {loading && (
        <div className="py-6 text-center font-mono text-[11px] text-noc-muted">
          Carregando histórico de energia…
        </div>
      )}

      {!loading && error && (
        <div className="py-6 text-center font-mono text-[11px] text-noc-err">
          Erro ao carregar energia: {error}
        </div>
      )}

      {!loading && !error && !hasPoints && (
        <div className="py-6 text-center font-mono text-[11px] text-noc-muted">
          Sem histórico de energia nas últimas 24h
        </div>
      )}

      {/* Resumo compacto (lista Dispositivos): chips + sparkline + link p/
          o gráfico completo. SEM gráfico grande → não estoura a tabela. */}
      {!loading && !error && hasPoints && compact && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1.5">
            <StatChip label="atual" value={f2(summary.last_power_w)} unit="W" accent />
            <StatChip label="média" value={f2(summary.avg_power_w)} unit="W" />
            <StatChip label="pico" value={f2(summary.max_power_w)} unit="W" />
            <StatChip label="V méd" value={f2(summary.avg_voltage_v)} unit="V" />
            <StatChip label="A méd" value={f3(summary.avg_current_a)} unit="A" />
            <StatChip label="amostras" value={summary.samples} />
          </div>
          <MiniSparkline points={data.points} />
          {onOpenDetail && (
            <button
              type="button"
              onClick={onOpenDetail}
              className="font-mono text-[10px] text-noc-accent hover:underline"
            >
              Ver gráfico 24h completo nos Detalhes →
            </button>
          )}
        </div>
      )}

      {/* Painel completo (DeviceDetail): cards + gráfico interativo. */}
      {!loading && !error && hasPoints && !compact && (
        <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-4">
            {/* ENERGY-DEVICE-CHART-1 — leitura ATUAL (última) em destaque. */}
            <StatCard label="Tensão atual" value={f2(summary.last_voltage_v)} unit="V" accent />
            <StatCard label="Corrente atual" value={f3(summary.last_current_a)} unit="A" accent />
            <StatCard label="Potência atual" value={f2(summary.last_power_w)} unit="W" accent />
            <StatCard label="Consumo est." value={f1(summary.energy_wh_24h_est)} unit="Wh/d" />
            <StatCard label="Tensão média" value={f2(summary.avg_voltage_v)} unit="V" />
            <StatCard label="Corrente média" value={f3(summary.avg_current_a)} unit="A" />
            <StatCard label="Potência média" value={f2(summary.avg_power_w)} unit="W" />
            <StatCard label="Pico 24h" value={f2(summary.max_power_w)} unit="W" />
          </div>
          <VACurrentChart points={data.points} bucketMinutes={data.bucket_minutes} />
          <div className="mt-1 text-right font-mono text-[9px] text-noc-faint">
            {summary.samples} amostras · consumo est. = potência média × 24h (estimativa)
          </div>
        </>
      )}
    </section>
  );
}
